import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSubmissions, getSubmission, createSubmission, updateSubmission, getFundSubmissions, getFundPeriods, getSubmissionEmailTimeline, sendQuarterlyEmail } from '@lingaai/supabase-client';
import type { Submission } from '@lingaai/types';

export function useSubmissions(portfolioCompanyId?: string) {
  return useQuery({
    queryKey: ['submissions', portfolioCompanyId],
    queryFn: () => getSubmissions(portfolioCompanyId),
  });
}

export function useSubmission(submissionId: string) {
  return useQuery({
    queryKey: ['submissions', submissionId],
    queryFn: () => getSubmission(submissionId),
    enabled: !!submissionId,
  });
}

export function useCreateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { portfolio_company_id: string; period: string; source_type: 'upload' | 'manual'; tenant_id?: string }) => createSubmission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
}

export function useUpdateSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submissionId, updates }: { submissionId: string; updates: Partial<Submission> }) => updateSubmission(submissionId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({
        queryKey: ['submissions', variables.submissionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['submission-details', variables.submissionId],
      });
    },
  });
}

export function useFundSubmissions(fundId: string, period: string) {
  return useQuery({
    queryKey: ['fund-submissions', fundId, period],
    queryFn: () => getFundSubmissions(fundId, period),
    enabled: !!fundId && !!period,
    staleTime: 0,
  });
}

export function useSubmissionEmailTimeline(submissionId: string | null) {
  return useQuery({
    queryKey: ['submission-email-timeline', submissionId],
    queryFn: () => getSubmissionEmailTimeline(submissionId!),
    enabled: !!submissionId,
  });
}

export function useFundPeriods(fundId: string) {
  return useQuery({
    queryKey: ['fund-periods', fundId],
    queryFn: () => getFundPeriods(fundId),
    enabled: !!fundId,
  });
}

/**
 * Hook to send quarterly email to portfolio company
 * Creates submission and sends initial metric request email
 */
export function useSendQuarterlyEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      portfolioCompanyId,
      tenantId,
      isTrial = false
    }: {
      portfolioCompanyId: string;
      tenantId: string;
      isTrial?: boolean;
    }) => sendQuarterlyEmail(portfolioCompanyId, tenantId, isTrial),

    onSuccess: () => {
      // Invalidate submissions queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['fund-submissions'] });
    },
  });
}
